import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@lognai/synapse-design-system-v2';
import { ShieldAlert } from '@lognai/synapse-design-system-v2/icons';
import { matchesPermission } from '@lognai/plugin-sdk';
import { useAuth } from '../hooks/useAuth';

/**
 * A route permission requirement: either a bare permission code (`'order:sales:read'`)
 * or a `{ pattern }` object (UIPermission). Mirrors how plugin routes declare permissions.
 */
type RoutePermission = string | { pattern: string };

interface RoutePermissionGuardProps {
  /** Permissions declared on the route (any one satisfies access). */
  permissions?: RoutePermission[];
  children: ReactNode;
}

/**
 * Guards a route's element against the active account's permissions.
 *
 * The sidebar already hides routes a user can't access, but a user can still reach
 * a route by typing the URL or following a stale link. This blocks that path with a
 * clean "no access" screen instead of letting the page mount and the API 403. The
 * backend remains the source of truth; this is a UX guard, not the security boundary.
 *
 * Access is granted when the route declares no permissions, or the user satisfies at
 * least one of them (matching the sidebar's "any" semantics).
 */
export function RoutePermissionGuard({ permissions, children }: RoutePermissionGuardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const requiredPermissions = (permissions ?? []).map((permission) =>
    typeof permission === 'string' ? permission : permission.pattern
  );

  if (requiredPermissions.length > 0) {
    const userPermissions = user?.permissions ?? [];
    // Reuse the shared matcher, but over the reactive `user.permissions` from
    // useAuth (not the localStorage snapshot) so the guard re-renders on switch.
    const authorized = requiredPermissions.some((required) =>
      matchesPermission(userPermissions, required)
    );

    if (!authorized) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
          <ShieldAlert className="h-12 w-12 text-muted-foreground" />
          <div>
            <h2 className="text-lg font-semibold">You don't have access to this page</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your account doesn't have permission to view this section.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/workspace')}>
            Go to Workspace
          </Button>
        </div>
      );
    }
  }

  return <>{children}</>;
}

export default RoutePermissionGuard;
